import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Calendar } from "@/components/ui/calendar";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { CalendarDays, X } from "lucide-react";
import { toast } from "sonner";
import { runRecipeDays, waitForRun } from "@/lib/api";

function toISO(d) {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function lastNWeekdays(n) {
  const out = [];
  const d = new Date();
  d.setDate(d.getDate() - 1); // start from yesterday
  while (out.length < n) {
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6) out.push(new Date(d));
    d.setDate(d.getDate() - 1);
  }
  return out.reverse();
}

export default function DaysPickerRunDialog({ recipe, onRunning }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [feedCutting, setFeedCutting] = useState(true);
  const nav = useNavigate();

  const hasDateStep = useMemo(
    () => (recipe.steps || []).some((s) => s.type === "fill" && s.is_date),
    [recipe.steps]
  );

  const sortedSelected = useMemo(
    () => [...(selected || [])].sort((a, b) => a - b),
    [selected]
  );

  function clear() { setSelected([]); }
  function removeOne(date) {
    setSelected((prev) => prev.filter((d) => d.getTime() !== date.getTime()));
  }

  async function onRun() {
    if (!selected.length) return toast.error("Selecciona pelo menos um dia.");
    setRunning(true);
    onRunning?.(true);
    try {
      const dates = sortedSelected.map(toISO);
      const started = await runRecipeDays(recipe.id, dates, feedCutting);
      setProgress({ done: 0, total: dates.length });
      const run = await waitForRun(started, (p) => p && setProgress(p));
      if (run.status === "error") {
        toast.error(`A captura falhou: ${run.error || "erro desconhecido"}`);
      } else if (run.status === "partial") {
        toast.warning(`Captura parcial: alguns dias falharam.`);
      } else {
        const okDays = (run.per_day || []).filter((p) => p.status === "success").length;
        toast.success(`Recolhidos ${run.rows?.length || 0} registos em ${okDays}/${dates.length} dia(s).`);
      }
      const dupDays = (run.per_day || []).filter((p) => p.bridge?.reason === "duplicate");
      if (dupDays.length) {
        toast.warning(
          `${dupDays.length} dia(s) não gravado(s) na Máquina de Corte: o CRM devolveu os mesmos cortes de outro dia (${dupDays.map((p) => p.label || p.date).join(", ")}). Tente novamente esses dias.`,
          { duration: 12000 },
        );
      }
      setOpen(false);
      nav(`/runs/${run.id}`);
    } catch (e) {
      toast.error(`Falha: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRunning(false);
      setProgress(null);
      onRunning?.(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="recipe-card-days-run-button" title="Captar dados em dias específicos">
          <CalendarDays className="h-3.5 w-3.5 mr-1"/> Dias
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Captar dados em dias específicos</DialogTitle>
          <DialogDescription>
            Selecciona um ou mais dias no calendário. Para cada dia, a rotina é executada com a data substituída nos passos marcados como <span className="mono">data</span>. Cada dia gera o seu próprio registo na Máquina de Corte.
          </DialogDescription>
        </DialogHeader>

        {!hasDateStep && (
          <div className="text-xs rounded-lg border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-3 text-foreground">
            ⚠️ Esta rotina ainda não tem passos <span className="mono">fill</span> marcados como “data”. Edita a rotina, abre o editor de cada campo de data e activa o toggle <span className="font-medium">“Campo de data”</span> escolhendo o formato (DD/MM/YYYY, YYYY-MM-DD…). Sem isso o sistema executa o passo com o valor original e todos os dias devolvem o mesmo resultado.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-4">
          <div className="border border-border rounded-lg p-1">
            <Calendar
              mode="multiple"
              selected={selected}
              onSelect={(days) => setSelected(days || [])}
              disabled={{ after: new Date() }}
              weekStartsOn={1}
              data-testid="days-picker-calendar"
            />
          </div>
          <div className="min-w-0 flex flex-col gap-3">
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" type="button" onClick={() => setSelected(lastNWeekdays(5))} data-testid="days-picker-preset-5">Últimos 5 úteis</Button>
              <Button size="sm" variant="outline" type="button" onClick={() => setSelected(lastNWeekdays(10))} data-testid="days-picker-preset-10">Últimos 10 úteis</Button>
              <Button size="sm" variant="ghost" type="button" onClick={clear} disabled={!selected.length} data-testid="days-picker-clear">Limpar</Button>
            </div>

            <div className="text-xs text-muted-foreground">
              {selected.length === 0
                ? "Nenhum dia seleccionado."
                : `${selected.length} dia(s) seleccionado(s).`}
            </div>
            <ScrollArea className="h-[180px] rounded-md border border-border bg-muted/30 p-2">
              <div className="flex flex-wrap gap-1.5">
                {sortedSelected.map((d) => (
                  <Badge key={d.getTime()} variant="outline" className="gap-1 pl-2 pr-1 py-0.5">
                    {toISO(d)}
                    <button
                      type="button"
                      aria-label={`Remover ${toISO(d)}`}
                      onClick={() => removeOne(d)}
                      className="ml-1 rounded-sm hover:bg-foreground/10 p-0.5"
                    >
                      <X className="h-3 w-3"/>
                    </button>
                  </Badge>
                ))}
              </div>
            </ScrollArea>

            {recipe.feed_cutting_simulator && (
              <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2">
                <div className="text-xs">
                  <div className="font-medium">Alimentar Máquina de Corte</div>
                  <div className="text-muted-foreground">Cada dia substitui o documento dessa data.</div>
                </div>
                <Switch checked={feedCutting} onCheckedChange={setFeedCutting} data-testid="days-picker-feed-cutting"/>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={running}>Cancelar</Button>
          <Button onClick={onRun} disabled={running || !selected.length} data-testid="days-picker-run-confirm">
            {running ? (progress ? `A correr… dia ${Math.min(progress.done + 1, progress.total)}/${progress.total}` : "A correr… (pode demorar)") : `Correr ${selected.length || ""} dia(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
